import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../lib/api';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { ChevronRight, BarChart3, Loader2, ArrowUpDown, TrendingDown, AlertTriangle, CheckCircle, Clock, Undo2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const PAYMENT_LABELS = { monthly: 'Monthly', upfront: 'Upfront', upon_request: 'Upon Request' };

function formatEUR(amount) {
  return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(amount);
}

function formatMonth(monthStr) {
  const [year, month] = monthStr.split('-');
  const date = new Date(parseInt(year), parseInt(month) - 1);
  return date.toLocaleDateString('en-GB', { month: 'short', year: 'numeric' });
}

export default function BudgetDashboardPage() {
  const [analytics, setAnalytics] = useState(null);
  const [reimbursements, setReimbursements] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState('utilization');
  const [markingReimbursed, setMarkingReimbursed] = useState(null);
  const navigate = useNavigate();
  const { isAdmin } = useAuth();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [analyticsRes, reimbRes] = await Promise.all([
        api.getBudgetAnalytics(),
        api.getReimbursements(),
      ]);
      setAnalytics(analyticsRes.data);
      setReimbursements(reimbRes.data);
    } catch { toast.error('Failed to load budget data'); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleMarkReimbursed = async (month, personEmail) => {
    setMarkingReimbursed(`${personEmail}|${month}`);
    try {
      await api.markReimbursed(month, personEmail);
      toast.success(`Marked ${formatMonth(month)} as reimbursed`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to mark reimbursed');
    }
    setMarkingReimbursed(null);
  };

  const handleUndoReimbursement = async (month, personEmail) => {
    setMarkingReimbursed(`${personEmail}|${month}`);
    try {
      await api.undoReimbursement(month, personEmail);
      toast.success(`Reimbursement undone for ${formatMonth(month)}`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to undo');
    }
    setMarkingReimbursed(null);
  };

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
    </div>
  );
  if (!analytics) return null;

  const allSorted = [...(analytics.rubriques || [])].sort((a, b) => {
    if (sortBy === 'utilization') return b.utilization_pct - a.utilization_pct;
    if (sortBy === 'variance') return a.cumulative_variance - b.cumulative_variance;
    if (sortBy === 'budget') return b.budget_24m - a.budget_24m;
    return b.actual - a.actual;
  });

  // Separate budget rows from excluded rows (VAT)
  const sorted = allSorted.filter(r => !r.exclude_from_budget);
  const excludedRows = allSorted.filter(r => r.exclude_from_budget);

  const paceColor = analytics.is_over_pace ? 'text-red-400' : 'text-emerald-400';

  const persons = reimbursements?.persons || [];
  const hasReimbursementData = persons.some(p => p.months.length > 0);

  return (
    <div className="space-y-6 max-w-6xl" data-testid="budget-dashboard">
      {/* Header */}
      <div>
        <h1 className="font-heading text-2xl text-foreground tracking-tight">Search Budget Tracker</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Total Budget: {formatEUR(analytics.total_budget)} over 24 months &middot; Start: {analytics.budget_start_date || analytics.start_date} &middot; Month {analytics.elapsed_months}/24
        </p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-card border border-border rounded-sm p-4">
          <p className="text-[10px] text-foreground/40 uppercase tracking-wider">Total Budget</p>
          <p className="text-xl font-mono font-semibold text-foreground mt-1" data-testid="total-budget">{formatEUR(analytics.total_budget)}</p>
        </div>
        <div className="bg-card border border-border rounded-sm p-4">
          <p className="text-[10px] text-foreground/40 uppercase tracking-wider">Total Spent</p>
          <p className="text-xl font-mono font-semibold text-foreground mt-1" data-testid="total-spent">{formatEUR(analytics.total_actual)}</p>
        </div>
        <div className="bg-card border border-border rounded-sm p-4">
          <p className="text-[10px] text-foreground/40 uppercase tracking-wider">Pacing Budget to Date</p>
          <p className="text-xl font-mono font-semibold text-foreground mt-1" data-testid="cumulative-budget">{formatEUR(analytics.cumulative_budget_to_date)}</p>
          <p className="text-[10px] text-muted-foreground mt-0.5">{analytics.elapsed_months}/24 months &times; {formatEUR(analytics.total_budget)}</p>
        </div>
        <div className="bg-card border border-border rounded-sm p-4">
          <p className="text-[10px] text-foreground/40 uppercase tracking-wider">Cumulative Variance</p>
          <div className="flex items-center gap-2 mt-1">
            {analytics.is_over_pace
              ? <AlertTriangle className="w-4 h-4 text-red-400 shrink-0" />
              : <TrendingDown className="w-4 h-4 text-emerald-400 shrink-0" />}
            <p className={`text-xl font-mono font-semibold ${paceColor}`} data-testid="cumulative-variance">
              {analytics.cumulative_variance >= 0 ? '+' : ''}{formatEUR(analytics.cumulative_variance)}
            </p>
          </div>
          <p className="text-[10px] text-muted-foreground mt-0.5">{analytics.is_over_pace ? 'Over budget pace' : 'Under budget pace'}</p>
        </div>
      </div>

      {/* Rubrique Table */}
      <div className="bg-card border border-border rounded-sm overflow-x-auto">

        <div className="min-w-[700px]">
        <div className="flex items-center justify-between px-4 py-3 border-b border-border">
          <div className="flex items-center gap-2">
            <BarChart3 className="w-4 h-4 text-foreground/60" />
            <p className="font-heading text-base text-foreground">Budget Categories</p>
            <span className="text-xs text-muted-foreground font-mono">({analytics.cost_item_count} cost items)</span>
          </div>
          <Select value={sortBy} onValueChange={setSortBy}>
            <SelectTrigger className="bg-secondary border-border rounded-sm h-7 text-[11px] w-36">
              <ArrowUpDown className="w-3 h-3 mr-1" /><SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-card border-border rounded-sm">
              <SelectItem value="utilization" className="text-xs">Sort by Utilization</SelectItem>
              <SelectItem value="variance" className="text-xs">Sort by Pacing Variance</SelectItem>
              <SelectItem value="budget" className="text-xs">Sort by Budget</SelectItem>
              <SelectItem value="actual" className="text-xs">Sort by Actual</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Header */}
        <div className="grid grid-cols-12 gap-1 px-4 py-2 text-[10px] text-foreground/40 uppercase tracking-wider border-b border-border bg-secondary/30">
          <div className="col-span-3">Category</div>
          <div className="col-span-1 text-center">Payment</div>
          <div className="col-span-2 text-right">24m Budget</div>
          <div className="col-span-2 text-right">Spent</div>
          <div className="col-span-2 text-right">Pace Variance</div>
          <div className="col-span-2 text-right">Utilization</div>
        </div>

        {sorted.map(r => {
          const isOver = r.is_over_pace;
          let utilColor = 'text-muted-foreground';
          if (r.utilization_pct > 100) utilColor = 'text-red-400';
          else if (r.utilization_pct > 80) utilColor = 'text-amber-400';

          let barColor = 'bg-emerald-500';
          if (r.utilization_pct > 100) barColor = 'bg-red-500';
          else if (r.utilization_pct > 80) barColor = 'bg-amber-500';

          return (
            <button key={r.rubrique_id} onClick={() => navigate(`/budget/rubrique/${r.rubrique_id}`)}
              data-testid={`rubrique-row-${r.rubrique_id}`}
              className="w-full grid grid-cols-12 gap-1 px-4 py-3 border-b border-border hover:bg-secondary/20 transition-colors text-left items-center group">
              <div className="col-span-3 flex items-center gap-2">
                {isOver && <AlertTriangle className="w-3.5 h-3.5 text-red-400 shrink-0" />}
                <span className="text-sm text-foreground group-hover:text-blue-400 transition-colors truncate">{r.name}</span>
              </div>
              <div className="col-span-1 text-center">
                <span className="text-[9px] font-mono text-muted-foreground border border-border rounded px-1.5 py-0.5">
                  {PAYMENT_LABELS[r.payment] || r.payment}
                </span>
              </div>
              <div className="col-span-2 text-right font-mono text-sm text-foreground/80">{formatEUR(r.budget_24m)}</div>
              <div className="col-span-2 text-right font-mono text-sm text-foreground">{formatEUR(r.actual)}</div>
              <div className={`col-span-2 text-right font-mono text-sm ${isOver ? 'text-red-400' : 'text-emerald-400'}`}>
                {r.cumulative_variance >= 0 ? '+' : ''}{formatEUR(r.cumulative_variance)}
              </div>
              <div className="col-span-2 flex items-center justify-end gap-2">
                <div className="w-16 bg-secondary rounded-full h-1.5">
                  <div className={`h-1.5 rounded-full ${barColor}`}
                    style={{ width: `${Math.min(r.utilization_pct, 100)}%` }} />
                </div>
                <span className={`font-mono text-xs ${utilColor}`}>{r.utilization_pct}%</span>
                <ChevronRight className="w-3.5 h-3.5 text-muted-foreground/30 group-hover:text-foreground transition-colors" />
              </div>
            </button>
          );
        })}

        {/* VAT / Excluded rows — separated at the bottom */}
        {excludedRows.length > 0 && (
          <>
            <div className="px-4 py-2 border-b border-border bg-secondary/10">
              <p className="text-[9px] text-muted-foreground/50 uppercase tracking-widest font-mono">Pass-through / Receivable</p>
            </div>
            {excludedRows.map(r => (
              <button key={r.rubrique_id} onClick={() => navigate(`/budget/rubrique/${r.rubrique_id}`)}
                data-testid={`rubrique-row-${r.rubrique_id}`}
                className="w-full grid grid-cols-12 gap-1 px-4 py-3 border-b border-border hover:bg-secondary/10 transition-colors text-left items-center group opacity-60">
                <div className="col-span-3 flex items-center gap-2">
                  <span className="text-sm text-muted-foreground group-hover:text-blue-400 transition-colors truncate">{r.name}</span>
                  <span className="text-[8px] text-muted-foreground/40 font-mono border border-border/40 rounded px-1">NOT IN BUDGET</span>
                </div>
                <div className="col-span-1 text-center">
                  <span className="text-[9px] font-mono text-muted-foreground/50 border border-border/40 rounded px-1.5 py-0.5">
                    {PAYMENT_LABELS[r.payment] || r.payment}
                  </span>
                </div>
                <div className="col-span-2 text-right font-mono text-sm text-muted-foreground">{formatEUR(r.budget_24m)}</div>
                <div className="col-span-2 text-right font-mono text-sm text-muted-foreground">{formatEUR(r.actual)}</div>
                <div className="col-span-2 text-right font-mono text-sm text-muted-foreground">—</div>
                <div className="col-span-2 flex items-center justify-end gap-2">
                  <span className="font-mono text-xs text-muted-foreground/50">{r.utilization_pct}%</span>
                  <ChevronRight className="w-3.5 h-3.5 text-muted-foreground/20 group-hover:text-foreground transition-colors" />
                </div>
              </button>
            ))}
          </>
        )}
      </div>
      </div>

      {/* Reimbursement Tracker */}
      <div className="bg-card border border-border rounded-sm overflow-x-auto" data-testid="reimbursement-tracker">
        <div className="px-4 py-3 border-b border-border">
          <p className="font-heading text-base text-foreground">Reimbursement Tracker</p>
          <p className="text-[10px] text-muted-foreground mt-0.5">Monthly reimbursements for approved "upon request" expenses per team member</p>
        </div>

        {!hasReimbursementData ? (
          <div className="p-8 text-center">
            <Clock className="w-8 h-8 text-muted-foreground/40 mx-auto mb-2" />
            <p className="text-sm text-muted-foreground">No reimbursable expenses yet</p>
            <p className="text-[10px] text-muted-foreground/60 mt-1">Approved "upon request" expenses will appear here grouped by person and month</p>
          </div>
        ) : (
          <div>
            {persons.filter(p => p.months.length > 0).map(person => {
              const personTotal = person.months.reduce((sum, m) => sum + m.total, 0);
              const reimbursedTotal = person.months.filter(m => m.reimbursed).reduce((sum, m) => sum + m.total, 0);
              const pendingTotal = personTotal - reimbursedTotal;

              return (
                <div key={person.email} className="border-b border-border last:border-b-0">
                  {/* Person Header */}
                  <div className="px-4 py-3 bg-secondary/20 flex items-center justify-between" data-testid={`reimb-person-${person.email}`}>
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-blue-600/20 flex items-center justify-center text-blue-400 text-xs font-medium font-mono">
                        {person.name?.charAt(0)?.toUpperCase() || '?'}
                      </div>
                      <div>
                        <p className="text-sm font-medium text-foreground">{person.name}</p>
                        <p className="text-[10px] text-muted-foreground">{person.email}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4 text-right">
                      <div>
                        <p className="text-[10px] text-foreground/40 uppercase tracking-wider">Total</p>
                        <p className="text-sm font-mono text-foreground">{formatEUR(personTotal)}</p>
                      </div>
                      <div>
                        <p className="text-[10px] text-emerald-400/60 uppercase tracking-wider">Reimbursed</p>
                        <p className="text-sm font-mono text-emerald-400">{formatEUR(reimbursedTotal)}</p>
                      </div>
                      {pendingTotal > 0 && (
                        <div>
                          <p className="text-[10px] text-amber-400/60 uppercase tracking-wider">Pending</p>
                          <p className="text-sm font-mono text-amber-400">{formatEUR(pendingTotal)}</p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Monthly Rows */}
                  {person.months.map(m => (
                    <div key={m.month}
                      className={`grid grid-cols-12 gap-2 px-4 py-2.5 items-center ${
                        m.reimbursed ? 'bg-emerald-950/5' : ''
                      }`}
                      data-testid={`reimb-row-${person.email}-${m.month}`}>
                      <div className="col-span-2 text-sm font-mono text-foreground">{formatMonth(m.month)}</div>
                      <div className="col-span-2 text-sm font-mono text-foreground text-right">{formatEUR(m.total)}</div>
                      <div className="col-span-2 text-[10px] text-muted-foreground text-center">{m.item_count} item{m.item_count !== 1 ? 's' : ''}</div>
                      <div className="col-span-3 flex items-center justify-center">
                        {m.reimbursed ? (
                          <span className="flex items-center gap-1 text-[10px] text-emerald-400 border border-emerald-800/30 bg-emerald-900/10 rounded px-2 py-0.5 font-mono">
                            <CheckCircle className="w-3 h-3" /> REIMBURSED
                          </span>
                        ) : (
                          <span className="flex items-center gap-1 text-[10px] text-amber-400 border border-amber-800/30 bg-amber-900/10 rounded px-2 py-0.5 font-mono">
                            <Clock className="w-3 h-3" /> PENDING
                          </span>
                        )}
                      </div>
                      <div className="col-span-3 flex items-center justify-end gap-2">
                        {m.reimbursed && m.reimbursed_at && (
                          <span className="text-[9px] text-muted-foreground/50 font-mono">
                            {new Date(m.reimbursed_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}
                          </span>
                        )}
                        {isAdmin && !m.reimbursed && (
                          <Button
                            size="sm"
                            onClick={() => handleMarkReimbursed(m.month, person.email)}
                            disabled={markingReimbursed === `${person.email}|${m.month}`}
                            data-testid={`reimb-mark-${person.email}-${m.month}`}
                            className="bg-emerald-700 hover:bg-emerald-600 text-white rounded-sm text-[10px] h-7 px-2.5">
                            {markingReimbursed === `${person.email}|${m.month}`
                              ? <Loader2 className="w-3 h-3 animate-spin" />
                              : <><CheckCircle className="w-3 h-3 mr-1" /> Mark Reimbursed</>}
                          </Button>
                        )}
                        {isAdmin && m.reimbursed && (
                          <button
                            onClick={() => handleUndoReimbursement(m.month, person.email)}
                            disabled={markingReimbursed === `${person.email}|${m.month}`}
                            data-testid={`reimb-undo-${person.email}-${m.month}`}
                            className="text-muted-foreground/30 hover:text-amber-400 transition-colors" title="Undo reimbursement">
                            <Undo2 className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
